/** Past-due / quota-exhausted banner — Phase 2F-bill-c.
 *
 * Renders at the top of the dashboard when the org's subscription
 * needs attention. Reads from `/billing/me` (already cached by the
 * billing panel). Silent when status is active + minutes are within
 * allowance — no noise. */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import type { BillingMeResponse } from "@/lib/types";


export function BillingBanner() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["billing-me"],
    queryFn: async () => (await api.get<BillingMeResponse>(ApiPaths.billingMe)).data,
    // Past-due is the operator's problem to fix, not a high-churn
    // signal — refresh once a minute is enough.
    refetchInterval: 60_000,
    // Some users don't have billing.read; treat 403 as "nothing to show."
    retry: false,
  });

  if (!q.data) return null;
  const { subscription, usage } = q.data;

  const status = subscription.status;
  const planSlug = subscription.plan.slug;
  const noOverage =
    Number(subscription.plan.overage_per_minute_eur) === 0;
  const exhausted = noOverage && usage.minutes_remaining <= 0;

  if (status === "active" || status === "trialing") {
    if (!exhausted) return null;
  }

  let body: string;
  let tone: "warn" | "danger";
  if (status === "past_due") {
    tone = "danger";
    body = t("ui.billingBanner.pastDue");
  } else if (status === "canceled") {
    tone = "danger";
    body = t("ui.billingBanner.canceled");
  } else {
    // Free plan exhausted
    tone = "warn";
    body = t("ui.billingBanner.exhausted", { minutes: subscription.plan.minutes_included });
  }

  const className =
    tone === "danger"
      ? "bg-cvp-danger/15 ring-cvp-danger/40 text-nb-text"
      : "bg-cvp-warn/15 ring-cvp-warn/40 text-nb-text";

  return (
    <div
      className={`flex flex-wrap items-baseline justify-between gap-3 rounded-md p-3 ring-1 ${className}`}
      role="alert"
    >
      <p className="text-sm">{body}</p>
      <Link to="/admin/billing" className="btn-ghost text-xs">
        {t("ui.billingBanner.openBilling")}
      </Link>
      <span className="sr-only">plan-slug:{planSlug}</span>
    </div>
  );
}
